import i18n from "i18next";

export const READ_TIMEOUT_MESSAGE = "The request timed out. Check your connection and try again.";
export const WRITE_TIMEOUT_MESSAGE =
  "The request timed out, but the change may have gone through. Check the result before you try again.";

export const SERVER_MESSAGE_KEYS: Record<string, string> = {
  [READ_TIMEOUT_MESSAGE]: "common.requestTimeout",
  [WRITE_TIMEOUT_MESSAGE]: "common.writeTimeout",
  "Validation failed.": "serverError.validationFailed",
  "Malformed request.": "serverError.malformedRequest",
  "Forbidden.": "serverError.forbidden",
  "Internal server error.": "serverError.internalError",
  "The requested resource was not found.": "serverError.notFound",
  "Field is invalid": "serverError.fieldInvalid",
  "The current password is incorrect.": "serverError.currentPasswordIncorrect",
  "The new password must differ from the current one.": "serverError.newPasswordMustDiffer",
  "The password is incorrect.": "serverError.passwordIncorrect",
  "The password is required.": "serverError.passwordRequired",
  "The current password and the new password are required.":
    "serverError.currentAndNewPasswordRequired",
  "Invalid email or password.": "serverError.invalidEmailOrPassword",
  "An account with this email already exists.": "serverError.emailAlreadyExists",
  "Email and password are required.": "serverError.emailAndPasswordRequired",
  "Email is required.": "serverError.emailRequired",
  "Invalid email parameter": "serverError.invalidEmailParameter",
  "Please verify your email address before logging in.": "serverError.verifyEmailFirst",
  "This verification token has expired.": "serverError.verificationTokenExpired",
  "This verification token is invalid.": "serverError.verificationTokenInvalid",
  "Invalid or expired reset token.": "serverError.resetTokenInvalid",
  "Token and new password are required.": "serverError.tokenAndPasswordRequired",
  "Please complete the verification challenge to continue.": "serverError.challengeRequired",
  "Please wait at least 60 seconds before requesting another verification email.":
    "serverError.verificationEmailCooldown",
  "The bearer on this request matches no session of the account.":
    "serverError.bearerMatchesNoSession",
  "An authenticator code is required.": "serverError.authenticatorCodeRequired",
  "The authenticator code is not valid.": "serverError.authenticatorCodeInvalid",
  "Authenticator code verification failed.": "serverError.authenticatorVerificationFailed",
  "An authenticator is already set up. Remove it before adding a new one.":
    "serverError.authenticatorAlreadySetUp",
  "No authenticator is set up.": "serverError.noAuthenticator",
  "Authenticator codes are not configured on this server.":
    "serverError.authenticatorNotConfigured",
  "The authenticator setup could not be read. Start the setup again.":
    "serverError.authenticatorSetupUnreadable",
  "A recovery code is required.": "serverError.recoveryCodeRequired",
  "Invalid recovery code.": "serverError.recoveryCodeInvalid",
  "Recovery codes are only available when MFA is enabled.": "serverError.recoveryCodesNeedMfa",
  "No passkeys are registered for this account. Use a recovery code.":
    "serverError.noPasskeysRegistered",
  "No passkey is available to confirm. Use your authenticator app instead.":
    "serverError.noPasskeyToConfirm",
  "Passkey not found.": "serverError.passkeyNotFound",
  "Passkey sign-in failed.": "serverError.passkeySignInFailed",
  "Passkey verification failed.": "serverError.passkeyVerificationFailed",
  "Passkey registration could not be verified.": "serverError.passkeyRegistrationUnverified",
  "Passkeys are not configured on this server.": "serverError.passkeysNotConfigured",
  "Invalid WebAuthn authentication response.": "serverError.webauthnAuthenticationInvalid",
  "Invalid WebAuthn registration response.": "serverError.webauthnRegistrationInvalid",
  "A WebAuthn authentication response is required.": "serverError.webauthnAuthenticationRequired",
  "A WebAuthn registration response is required.": "serverError.webauthnRegistrationRequired",
  "The authentication challenge is invalid or has expired.":
    "serverError.authenticationChallengeExpired",
  "The sign-in challenge is invalid or has expired.": "serverError.signInChallengeExpired",
  "The registration challenge is invalid or has expired. Start the registration again.":
    "serverError.registrationChallengeExpired",
  "A pending login token is required.": "serverError.pendingLoginRequired",
  "The pending login is invalid or has expired. Please log in again.":
    "serverError.pendingLoginExpired",
  "No deletion is scheduled for this account.": "serverError.noDeletionScheduled",
  "Too many attempts. Please try again later.": "serverError.tooManyAttempts",
  "Too many login attempts. Please try again later.": "serverError.tooManyLoginAttempts",
  "Too many MFA attempts. Please try again later.": "serverError.tooManyMfaAttempts",
  "Too many registration attempts. Please try again later.":
    "serverError.tooManyRegistrationAttempts",
  "Too many requests. Please try again later.": "serverError.tooManyRequests",
  "Too many requests": "serverError.tooManyRequestsShort",
  "Too many password reset attempts. Please try again later.":
    "serverError.tooManyPasswordResetAttempts",
  "Too many password reset requests. Please try again later.":
    "serverError.tooManyPasswordResetRequests",
  "Too many verification email requests. Please try again later.":
    "serverError.tooManyVerificationEmailRequests",
};

export const FIELD_LABEL_KEYS: Record<string, string> = {
  email: "serverField.email",
  password: "serverField.password",
  currentPassword: "serverField.currentPassword",
  newPassword: "serverField.newPassword",
  name: "serverField.name",
  appName: "serverField.appName",
  permission: "serverField.permission",
  token: "serverField.token",
};

function lookup(key: string, fallback: string): string {
  if (!i18n.isInitialized) return fallback;
  try {
    const translated = i18n.t(key, { defaultValue: fallback });
    if (typeof translated !== "string" || !translated || translated === key) return fallback;
    return translated;
  } catch {
    return fallback;
  }
}

function ownKey(table: Record<string, string>, name: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(table, name) ? table[name] : undefined;
}

export function translateServerMessage(message: string): string;
export function translateServerMessage(message: string | undefined): string | undefined;
export function translateServerMessage(message: string | undefined): string | undefined {
  if (typeof message !== "string" || !message) return message;
  const key = ownKey(SERVER_MESSAGE_KEYS, message.trim());
  if (!key) return message;
  return lookup(key, message);
}

export function translateServerField(field: string): string | undefined {
  const key = ownKey(FIELD_LABEL_KEYS, field);
  if (!key) return undefined;
  const translated = lookup(key, "");
  return translated || undefined;
}
